<section
  class="composite-reuse-panel"
  *transloco="let t"
  [attr.aria-label]="t('composite_reuse_panel.aria_label')"
>
  @if (candidatesLoading()) {
    <!-- Skeleton mirrors the populated layout (coverage strip +
         candidate rows + gap-filler toggle) so its vertical footprint
         matches the loaded state. Prevents the modal from resizing
         when the fetch resolves. -->
    <div class="composite-reuse-panel__skeleton" aria-hidden="true">
      <div appSkeleton class="composite-reuse-panel__skeleton-coverage"></div>
      <div class="composite-reuse-panel__skeleton-section">
        <div appSkeleton class="composite-reuse-panel__skeleton-heading"></div>
        <div class="composite-reuse-panel__skeleton-rows">
          @for (_ of skeletonRows(); track $index) {
            <div class="composite-reuse-panel__skeleton-row">
              <div appSkeleton class="composite-reuse-panel__skeleton-check"></div>
              <div appSkeleton class="composite-reuse-panel__skeleton-line"></div>
            </div>
          }
        </div>
      </div>
      <div class="composite-reuse-panel__skeleton-section">
        <div appSkeleton class="composite-reuse-panel__skeleton-heading"></div>
        <div class="composite-reuse-panel__skeleton-gap">
          <div appSkeleton class="composite-reuse-panel__skeleton-check"></div>
          <div class="composite-reuse-panel__skeleton-gap-body">
            <div appSkeleton class="composite-reuse-panel__skeleton-line"></div>
            <div appSkeleton class="composite-reuse-panel__skeleton-subline"></div>
          </div>
        </div>
      </div>
      <span class="sr-only">{{ 'composite_reuse_panel.loading_candidates' | transloco }}</span>
    </div>
  } @else if (candidatesError()) {
    <div class="warn-banner reuse-banner" role="alert">
      <span>{{ 'composite_reuse_panel.error_hint' | transloco }}</span>
      <button type="button" class="reuse-banner__retry" (click)="onRetryClick()">
        {{ 'composite_reuse_panel.retry' | transloco }}
      </button>
    </div>
  } @else if (candidates().length === 0) {
    <div class="composite-reuse-panel__hint">
      <mat-icon aria-hidden="true">add_circle_outline</mat-icon>
      <span>{{ 'composite_reuse_panel.no_candidates_hint' | transloco }}</span>
    </div>
  } @else {
    <!-- Coverage header — segmented meter + state-tinted summary.
         Visual language aligned with the import-assistant reuse
         overlay (`.ia-reuse-overlay-coverage*`) so managers read
         composite coverage the same way in both surfaces. Ticks map
         1:1 to component refs; solid success = reused, striped info =
         will-be-gap-filled, muted = uncovered. -->
    @let cov = coverage();
    @let willFill = createGapFiller() && cov.uncoveredCount > 0;
    @let coveredTotal = cov.coveredCount + (willFill ? cov.uncoveredCount : 0);
    <div class="composite-reuse-panel__coverage">
      @if (cov.totalComponentCount > 1) {
        <div class="composite-reuse-panel__meter" role="presentation">
          <div class="composite-reuse-panel__meter-track">
            @for (_ of coverageTicks(cov.totalComponentCount); track $index) {
              <span
                class="composite-reuse-panel__meter-tick"
                [class.is-covered]="$index < cov.coveredCount"
                [class.is-gap-filled]="$index >= cov.coveredCount && $index < coveredTotal"
              ></span>
            }
          </div>
          <span class="composite-reuse-panel__meter-progress">
            {{
              'composite_reuse_panel.coverage_summary'
                | transloco: { covered: coveredTotal, total: cov.totalComponentCount }
            }}
          </span>
        </div>
      }
      <p
        class="composite-reuse-panel__coverage-summary"
        [attr.data-intent]="coverageIntent()"
        aria-live="polite"
      >
        <mat-icon aria-hidden="true">{{ coverageIcon() }}</mat-icon>
        <span>
          {{
            'composite_reuse_panel.coverage_summary'
              | transloco: { covered: cov.coveredCount, total: cov.totalComponentCount }
          }}
          @if (cov.uncoveredCount > 0 && !createGapFiller()) {
            <span class="composite-reuse-panel__warning">
              — {{ uncoveredWarningKey() | transloco: { count: cov.uncoveredCount } }}
            </span>
          }
        </span>
      </p>
    </div>

    <div class="composite-reuse-panel__section">
      <h4 class="composite-reuse-panel__section-title">
        {{ 'composite_reuse_panel.reuse_title' | transloco }}
      </h4>
      <p class="composite-reuse-panel__section-hint">
        {{ 'composite_reuse_panel.reuse_hint' | transloco }}
      </p>
      <ul class="composite-reuse-panel__candidates" role="list">
        @for (candidate of candidates(); track candidate.control_id) {
          <li
            class="composite-reuse-panel__candidate"
            [class.is-picked]="reusedIdSet().has(candidate.control_id)"
          >
            <label class="composite-reuse-panel__candidate-label">
              <input
                type="checkbox"
                class="composite-reuse-panel__candidate-check"
                [checked]="reusedIdSet().has(candidate.control_id)"
                [disabled]="readOnly()"
                (change)="onToggleCandidate(candidate.control_id, $any($event.target).checked)"
              />
              <span class="composite-reuse-panel__candidate-body">
                @if (candidate.control_reference) {
                  @let candIdentity = matchFrameworkIdentity(candidate);
                  <app-control-ref
                    [reference]="candidate.control_reference"
                    [frameworkType]="candIdentity?.frameworkType ?? null"
                    [frameworkVersion]="candIdentity?.frameworkVersion ?? null"
                    [frameworkName]="candIdentity?.frameworkName ?? null"
                  />
                }
                <span class="composite-reuse-panel__candidate-name">{{ candidate.name }}</span>
                @if (candidate.covers_component_refs?.length) {
                  <span class="composite-reuse-panel__candidate-covers">
                    {{
                      candidateCoversKey(candidate.covers_component_refs?.length ?? 0)
                        | transloco: { count: candidate.covers_component_refs?.length ?? 0 }
                    }}
                  </span>
                }
              </span>
            </label>
          </li>
        }
      </ul>
    </div>
  }

  @if (!candidatesLoading() && !candidatesError()) {
    <!-- Gap-filler card. Distinct dashed treatment communicates
         "this control doesn't exist yet — it will be minted" so it
         reads separately from existing-candidate rows. `+` icon in
         the title + "NEW" tag reinforce the create-vs-reuse dichotomy;
         info-tint on the picked border + tinted ref chips mark the
         uncovered refs the new control will cover. Mirrors the
         import-assistant reuse overlay's `is-gap-filler` treatment.
         Divider only when there's a reuse section above to divide
         from — in the empty-candidates state the card stands alone. -->
    @if (candidates().length > 0) {
      <div class="composite-reuse-panel__gap-divider" role="presentation">
        <span>{{ 'composite_reuse_panel.gap_filler_title' | transloco }}</span>
      </div>
    }
    <label
      class="composite-reuse-panel__gap-card"
      [class.is-picked]="createGapFiller() && candidates().length > 0"
      [class.is-locked]="candidates().length === 0"
    >
      <input
        type="checkbox"
        class="composite-reuse-panel__gap-check"
        [checked]="createGapFiller()"
        [disabled]="readOnly() || candidates().length === 0"
        (change)="onToggleGapFiller($any($event.target).checked)"
      />
      <span class="composite-reuse-panel__gap-body">
        <span class="composite-reuse-panel__gap-title">
          <mat-icon aria-hidden="true">add_circle_outline</mat-icon>
          {{ 'composite_reuse_panel.gap_toggle_label' | transloco }}
          <span class="composite-reuse-panel__gap-tag">
            {{ 'composite_reuse_panel.gap_new_tag' | transloco }}
          </span>
        </span>
        <span class="composite-reuse-panel__gap-desc">
          {{ gapDescriptionKey() | transloco: { count: coverage().uncoveredCount } }}
        </span>
        @if (coverage().uncoveredComponentRefs.length > 0 && createGapFiller()) {
          <span class="composite-reuse-panel__gap-refs" role="list">
            @for (ref of coverage().uncoveredComponentRefs; track ref) {
              <span class="composite-reuse-panel__gap-chip" role="listitem">{{ ref }}</span>
            }
          </span>
        }
      </span>
    </label>
  }

  @if (!readOnly() && showActions()) {
    <div class="composite-reuse-panel__actions">
      <button
        type="button"
        class="composite-reuse-panel__btn composite-reuse-panel__btn--secondary"
        (click)="onCreateNewOnlyClick()"
      >
        {{ 'composite_reuse_panel.create_new_only' | transloco }}
      </button>
      <button
        type="button"
        class="composite-reuse-panel__btn composite-reuse-panel__btn--primary"
        [disabled]="!canSave()"
        (click)="onSaveClick()"
      >
        {{ 'composite_reuse_panel.save' | transloco }}
      </button>
    </div>
  }
</section>
